import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Select, MenuItem, Box, Typography, CircularProgress } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import useLocales from '../../../hooks/useLocales';
import Iconify from '../../../components/Iconify';
import { ALL_ORGANIZATIONS_ID } from '../../../constants/mycolab';
import { getMyOrganizationRequest, setSelectedOrganization } from '../../../redux/slices/mycolab';

// ----------------------------------------------------------------------

const SelectStyle = styled(Select)(({ theme }) => ({
  height: 36,
  minWidth: 160,
  maxWidth: 220,
  backgroundColor: alpha(theme.palette.grey[500], 0.08),
  borderRadius: theme.shape.borderRadius,
  transition: theme.transitions.create(['background-color', 'box-shadow']),
  border: `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
  '& .MuiSelect-select': {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(4),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.grey[500], 0.12),
  },
  '&.Mui-focused': {
    backgroundColor: alpha(theme.palette.grey[500], 0.12),
    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.24)}`,
  },
}));

// ----------------------------------------------------------------------

export default function OrganizationSelector() {
  const dispatch = useDispatch();
  const { translate } = useLocales();
  const hasFetchedRef = useRef(false);

  const { organizations, selectedOrganization, loading, loaded } = useSelector(
    (state) => state.mycolab.organization
  );

  // Always fetch organizations on mount to validate persisted state
  // This ensures persisted organization selection is validated against current backend data
  // Note: do NOT gate on `loading` — it is persisted to localStorage and can be stale (true)
  // after a page reload, which would prevent the fetch from ever firing.
  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      dispatch(getMyOrganizationRequest());
    }
  }, [dispatch]);

  // Build options: add "ALL" only if more than one organization exists
  const options = useMemo(() => {
    const baseOptions = organizations || [];
    if (baseOptions.length <= 1) {
      return baseOptions;
    }
    const allOption = {
      id: ALL_ORGANIZATIONS_ID,
      name: translate('mycolab.all_organizations'),
      isAll: true,
    };
    return [allOption, ...baseOptions];
  }, [organizations, translate]);

  const value = selectedOrganization?.id ? String(selectedOrganization.id) : '';
  const hasOptions = options.length > 0;

  const handleChange = (event) => {
    const nextValue = event.target.value;
    if (nextValue === ALL_ORGANIZATIONS_ID) {
      dispatch(
        setSelectedOrganization({
          id: ALL_ORGANIZATIONS_ID,
          name: translate('mycolab.all_organizations'),
          isAll: true,
        })
      );
    } else {
      const match = organizations.find((org) => String(org.id) === String(nextValue));
      dispatch(setSelectedOrganization(match || null));
    }
  };

  // Loading state
  if (!hasOptions && loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">
          {translate('common.loading')}
        </Typography>
      </Box>
    );
  }

  // No organizations
  if (!hasOptions) {
    return null;
  }

  // Single organization - show as non-interactive label
  if (options.length === 1) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          borderRadius: 1,
          backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.08),
          border: (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
        }}
      >
        <Iconify
          icon="eva:briefcase-outline"
          width={18}
          height={18}
          sx={{ color: 'text.secondary' }}
        />
        <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
          {options[0].name}
        </Typography>
      </Box>
    );
  }

  // Multiple organizations - show dropdown
  return (
    <SelectStyle
      value={value || ALL_ORGANIZATIONS_ID}
      onChange={handleChange}
      displayEmpty
      renderValue={(selected) => {
        const selectedOrg = options.find((opt) => String(opt.id) === String(selected));
        const label = (() => {
          if (!selectedOrg) {
            return '';
          }
          if (selectedOrg.isAll) {
            return translate('mycolab.all_organizations');
          }
          return selectedOrg.name;
        })();
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Iconify icon="eva:briefcase-outline" width={18} height={18} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {label}
            </Typography>
          </Box>
        );
      }}
    >
      {options.map((option) => (
        <MenuItem key={option.id} value={String(option.id)}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Iconify
              icon={option.isAll ? 'eva:layers-outline' : 'eva:briefcase-outline'}
              width={20}
              height={20}
            />
            <Typography variant="body2">
              {option.isAll ? translate('mycolab.all_organizations') : option.name}
            </Typography>
          </Box>
        </MenuItem>
      ))}
    </SelectStyle>
  );
}
